import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Grid } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import queryString from 'query-string'
import dayjs, { Dayjs } from 'dayjs'

import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import { CategoryFormModal } from '../../components/CategoryFormModal'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../types/pagination'
import CategoriesTable from '../../components/CategoriesTable'
import { ICategory } from '../../../../types/category'
import { useNavigate } from 'react-router-dom'
import PageUrls from '../../../../constants/page-urls'
import CategoryFilter, { IFilterMapped } from '../../components/CategoryFilter'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../constants/global'
import { message } from 'antd'
import * as XLSX from 'xlsx'
import { ERRORS_MESSAGE_MAPPER } from '../../../../constants/errors'

const Menus = [
  {
    breadcrumbName: 'รายการสินค้า',
    title: 'รายการสินค้า',
    path: PageUrls.Product,
  },
  {
    breadcrumbName: 'รายงาน ลด-เพิ่ม สินค้า',
    title: 'รายงาน ลด-เพิ่ม สินค้า',
    path: PageUrls.Stock,
  },
]

const CategoriesListPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)
  const [isModalCreateCategory, setIsModalCreateCategory] =
    React.useState(false)
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
    search: queryStr.search?.length
      ? String(queryStr.search).toLowerCase()
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
    search: queryStr.search?.length
      ? String(queryStr.search).toLowerCase()
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    const filter = {
      ...newFilter,
      page: 1,
    }
    setFilter(filter)
    setSearchFilter(filter)
    const newQuery = queryString.stringify({ ...filter })
    navigate(`${PageUrls.Category}?${newQuery}`, {
      replace: true,
    })
  }

  const handlePageChange = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Category}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const categoryList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: searchFilter,
  })

  const handleOnDownload = useCallback(async (date: Dayjs) => {
    let loop = true
    let page = 1,
      perPage = MAX_DONWLOAD_PAGE_SIZE
    const data: ICategory[] = []
    while (loop) {
      try {
        const result = await axiosInstance.get('/category', {
          params: {
            ...searchFilter,
            page,
            perPage,
          },
        })
        page++
        if (!result?.data?.records.length) {
          loop = false
          break
        }
        data.push(...result.data.records)
        if (result?.data?.count < perPage) {
          loop = false
          break
        }
      } catch (e) {
        message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
        break
      }
    }
    if (!data.length) {
      message.warning('ไม่พบข้อมูล')
      return
    }
    const rows = data?.map((dt: ICategory) => {
      return {
        ชื่อหมวดหมู่: dt.name,
        รายละเอียด: dt.description,
        วันที่เพิ่ม: dayjs(dt.createdAt).format('DD/MM/YYYY HH:mm'),
        วันที่แก้ไข: dayjs(dt.updatedAt).format('DD/MM/YYYY HH:mm'),
      }
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
    XLSX.writeFile(wb, `categories.xlsx`)
    message.success('ดาวน์โหลดสำเร็จ')
  }, [searchFilter])

  const onUpdateCategorySuccess = useCallback(async () => {
    try {
      await categoryList.mutate()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }, [filter])

  return (
    <ContentWrapper>
      <Helmet title="รายการหมวดหมู่" />
      <CategoryFormModal
        isOpen={isModalCreateCategory}
        onClose={(visible: boolean) => setIsModalCreateCategory(visible)}
        onUpdateCategorySuccess={onUpdateCategorySuccess}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="หมวดหมู่สินค้า"
            routes={Menus}
            descriptions="รายการหมวดหมู่สินค้า"
            tags={[
              {
                title: 'จำนวน',
                color: 'info',
                value: categoryList.data?.count ?? 0,
                classifier: 'รายการ',
              },
            ]}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setIsModalCreateCategory(true)}
          >
            หมวดหมู่สินค้า
          </Button>
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <CategoryFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
        />
        <CategoriesTable
          isLoading={categoryList.isLoading}
          data={categoryList.data?.records ?? []}
          onUpdateCategorySuccess={onUpdateCategorySuccess}
          filter={{ ...filter }}
          onFilterChange={handleSearch}
          handlePageChange={handlePageChange}
          total={categoryList.data?.count ?? 0}
        />
      </Card>
    </ContentWrapper>
  )
}

export default CategoriesListPage
